import { useState, useEffect, useCallback } from 'react';
import { useCurrency } from '@/hooks/useCurrency';
import {
  getCurrencyQuickAmounts,
  getCurrencySymbol,
  getCurrencyStep,
  getCurrencyMinAmount,
} from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Loader2, Plus, Minus, RotateCcw } from 'lucide-react';
import type { Goal } from '@/lib/types';

interface ContributeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: Goal | null;
  onContribute: (goalId: string, amount: number) => Promise<void>;
  isLoading?: boolean;
}

/**
 * [T59P0] Redesigned contribution sheet with stacking quick-amounts,
 * plus/minus adjustment buttons, and dynamic button widths.
 * Clicking a preset now ADDS to the running total instead of replacing it.
 */
export default function ContributeDialog({
  open,
  onOpenChange,
  goal,
  onContribute,
  isLoading = false,
}: ContributeDialogProps) {
  const [amount, setAmount] = useState('');
  const { currency, fmt } = useCurrency();
  const currencySymbol = getCurrencySymbol(currency);
  const quickAmounts = getCurrencyQuickAmounts(currency);
  const step = getCurrencyStep(currency);
  const minAmount = getCurrencyMinAmount(currency);

  // Reset amount when dialog opens/closes or goal changes
  useEffect(() => {
    if (open) setAmount('');
  }, [open, goal]);

  /** Add a quick-amount to the running total (stacking behaviour) */
  const addQuickAmount = useCallback((qa: number) => {
    setAmount((prev) => {
      const current = parseFloat(prev) || 0;
      return String(current + qa);
    });
  }, []);

  /** Subtract a quick-amount from the running total (floor at 0) */
  const subtractQuickAmount = useCallback((qa: number) => {
    setAmount((prev) => {
      const current = parseFloat(prev) || 0;
      const next = Math.max(current - qa, 0);
      return next === 0 ? '' : String(next);
    });
  }, []);

  /** Reset the amount to zero */
  const resetAmount = useCallback(() => setAmount(''), []);

  if (!goal) return null;

  const remaining = goal.targetAmount - goal.currentAmount;
  const percentage =
    goal.targetAmount > 0
      ? Math.round((goal.currentAmount / goal.targetAmount) * 100)
      : 0;
  const numAmount = parseFloat(amount) || 0;

  // Preview: what the new total would be after this contribution
  const previewTotal = goal.currentAmount + numAmount;
  const previewPercentage =
    goal.targetAmount > 0
      ? Math.min(Math.round((previewTotal / goal.targetAmount) * 100), 100)
      : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (numAmount > 0) {
      await onContribute(goal.id, numAmount);
      setAmount('');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            Contribute to{' '}
            {goal.emoji && <span className="mr-1">{goal.emoji}</span>}
            {goal.name}
          </SheetTitle>
          <SheetDescription>
            Add funds towards your goal. Current progress: {percentage}%
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          {/* Progress display */}
          <div>
            <Progress value={Math.min(percentage, 100)} className="h-3" />
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
              <span>{fmt(goal.currentAmount)}</span>
              <span>{fmt(goal.targetAmount)}</span>
            </div>
            <p className="text-sm text-muted-foreground text-center mt-1">
              {fmt(remaining)} remaining
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Amount input with plus/minus controls */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="contribute-amount">
                  Amount ({currencySymbol})
                </Label>
                {numAmount > 0 && (
                  <button
                    type="button"
                    onClick={resetAmount}
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  disabled={numAmount <= 0}
                  onClick={() =>
                    subtractQuickAmount(quickAmounts[0] ?? 1)
                  }
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="contribute-amount"
                  type="number"
                  step={step}
                  min={minAmount}
                  placeholder="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="text-center text-lg font-semibold"
                  autoFocus
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => addQuickAmount(quickAmounts[0] ?? 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Preview bar — shows projected progress after contribution */}
            {numAmount > 0 && (
              <div className="rounded-md bg-muted/50 p-2.5 text-center text-xs text-muted-foreground">
                After contribution: {fmt(previewTotal)} ({previewPercentage}%)
              </div>
            )}

            {/* Quick amounts — stacking presets with +/– per row */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                Quick add (tap to stack)
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {quickAmounts.map((qa) => (
                  <div key={qa} className="inline-flex rounded-md border border-input overflow-hidden">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-1.5 rounded-none border-r border-input hover:bg-destructive/10"
                      onClick={() => subtractQuickAmount(qa)}
                      disabled={numAmount < qa}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2.5 rounded-none font-medium tabular-nums"
                      onClick={() => addQuickAmount(qa)}
                    >
                      {fmt(qa)}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-1.5 rounded-none border-l border-input hover:bg-primary/10"
                      onClick={() => addQuickAmount(qa)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Fill remaining shortcut */}
            {remaining > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() =>
                  setAmount(String(Number(remaining.toFixed(2))))
                }
              >
                Fill remaining ({fmt(remaining)})
              </Button>
            )}

            <SheetFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || numAmount <= 0}
              >
                {isLoading && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Contribute {numAmount > 0 ? fmt(numAmount) : ''}
              </Button>
            </SheetFooter>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
